import { useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import { useLocale } from "@/context/LanguageContext";

interface Props {
  open: boolean;
  periodLabel: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirms re-running an existing report. The current PDF is deleted from
 * S3 server-side and the row is re-enqueued — this consumes one slot from
 * the org's daily generation quota.
 */
export function RegenerateConfirmDialog({
  open,
  periodLabel,
  loading = false,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useLocale();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={loading ? undefined : onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="regenerate-confirm-title"
      >
        <div className="mb-3 flex items-start justify-between">
          <h2
            id="regenerate-confirm-title"
            className="text-lg font-semibold text-foreground"
          >
            {t("reports.regenerate.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="-m-1 rounded-md p-1 text-muted-foreground transition hover:text-foreground disabled:opacity-50"
            aria-label={t("common.cancel")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          {t("reports.regenerate.body", { period: periodLabel })}
        </p>

        <div className="mt-4 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <p>{t("reports.regenerate.notice")}</p>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading
              ? t("reports.regenerate.submitting")
              : t("reports.regenerate.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
